import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useEmpresa } from '../../../context/EmpresaContext';
import { useApi } from '../../../hooks/useApi';
import { apiRequest } from '../../../utils/api';
import './Admin.css';

const Usuarios = () => {
  const { isAdmin, isEmpresaAdmin, canCreateAdmins, usuario, loading: authLoading } = useAuth();
  const { empresaActual, loading: empresasLoading } = useEmpresa();
  const { get, post, put, del } = useApi();

  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    nombres: '',
    apellidos: '',
    edad: '',
    cedulaRuc: '',
    ciudad: '',
    parroquia: '',
    usuario: '',
    email: '',
    password: '',
    encargo: 'trabajadores',
    rol: 'trabajadores',
    empresaId: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Limpiar empresaId cuando se cambia a rol conexadmin
  useEffect(() => {
    if (formData.rol === 'conexadmin') {
      setFormData(prev => ({ ...prev, empresaId: '' }));
    }
  }, [formData.rol]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Cargar usuarios usando apiRequest

      const response = await apiRequest('/users');

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const usuariosData = await response.json();

      setUsuarios(usuariosData);

      // Cargar empresas para mostrar nombres en la tabla y para el formulario

      const empresasResponse = await apiRequest('/empresas');

      if (empresasResponse.ok) {
        const empresasData = await empresasResponse.json();

        setEmpresas(empresasData);
      } else {

      }

      setError(null);
    } catch (err) {
      console.error('❌ Error en loadData:', err);
      setError(`Error al cargar datos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Función para validar límites de roles por empresa
  const validateRoleLimits = () => {
    // Validar que se requiera empresaId para roles no-conexadmin
    if (formData.rol !== 'conexadmin' && (!formData.empresaId || formData.empresaId === '')) {
      return 'Debe seleccionar una empresa para este rol.';
    }

    if (formData.rol === 'conexadmin' || formData.rol === 'cliente') {
      return null; // No aplicar validación de límites para estos casos
    }

    // empresaId es un string (Firestore ID), solo verificar que no esté vacío
    const empresaId = formData.empresaId;
    if (!empresaId || empresaId.trim() === '') {
      return 'Debe seleccionar una empresa válida.';
    }

    // Comparar como strings ya que Firestore usa IDs tipo string
    const usuariosEmpresa = usuarios.filter(u => String(u.empresaId) === String(empresaId));

    // Si estamos editando, excluir el usuario actual del conteo
    const usuariosParaContar = editingUsuario
      ? usuariosEmpresa.filter(u => u.id !== editingUsuario.id)
      : usuariosEmpresa;

    const adminCount = usuariosParaContar.filter(u => u.rol === 'administrador').length;
    const workerCount = usuariosParaContar.filter(u => u.rol === 'trabajadores').length;

    if (formData.rol === 'administrador' && adminCount >= 1) {
      return 'Esta empresa ya tiene 1 administrador. Cada empresa solo puede tener 1 administrador.';
    }

    if (formData.rol === 'trabajadores' && workerCount >= 3) {
      return 'Esta empresa ya tiene 3 trabajadores. Cada empresa solo puede tener 3 trabajadores.';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      // Validar límites de roles antes de enviar
      const roleValidationError = validateRoleLimits();
      if (roleValidationError) {
        setError(roleValidationError);
        return;
      }

      const submitData = {
        ...formData,
        edad: parseInt(formData.edad, 10)
      };

      // Si estamos editando y la contraseña está vacía, no enviarla
      if (editingUsuario && (!submitData.password || submitData.password.trim() === '')) {
        delete submitData.password;
      }

      // Manejar empresaId según el rol
      if (formData.rol === 'conexadmin') {
        // AdminConex no debe tener empresaId - eliminar completamente el campo
        delete submitData.empresaId;
      } else {
        // Otros roles deben tener empresaId (string de Firestore)
        if (formData.empresaId && formData.empresaId !== '') {
          submitData.empresaId = formData.empresaId;
        } else {
          throw new Error('Los usuarios no-AdminConex deben estar asociados a una empresa');
        }
      }



      const token = localStorage.getItem('token');


      const endpoint = editingUsuario
        ? `/users/${editingUsuario.id}`
        : '/users';

      const method = editingUsuario ? 'PUT' : 'POST';

      const response = await apiRequest(endpoint, {
        method,
        body: JSON.stringify(submitData)
      });



      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ Error response:', errorData);

        // Intentar parsear el error como JSON
        let errorMessage = `Error ${response.status}: ${errorData}`;
        try {
          const errorJson = JSON.parse(errorData);
          if (errorJson.error) {
            errorMessage = errorJson.error;
          }
        } catch (e) {
          // Si no es JSON, usar el texto tal como está
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();


      setShowForm(false);
      setEditingUsuario(null);
      setFormData({
        nombres: '',
        apellidos: '',
        edad: '',
        cedulaRuc: '',
        ciudad: '',
        parroquia: '',
        usuario: '',
        email: '',
        password: '',
        encargo: 'trabajadores',
        rol: 'trabajadores',
        empresaId: ''
      });
      loadData();
    } catch (err) {
      setError(`Error al guardar usuario: ${err.message}`);
      console.error('Error saving usuario:', err);
    }
  };

  const handleEdit = (usuario) => {
    setEditingUsuario(usuario);
    setFormData({
      nombres: usuario.nombres || '',
      apellidos: usuario.apellidos || '',
      edad: usuario.edad ? usuario.edad.toString() : '',
      cedulaRuc: usuario.cedulaRuc || '',
      ciudad: usuario.ciudad || '',
      parroquia: usuario.parroquia || '',
      usuario: usuario.usuario || '',
      email: usuario.email || '',
      password: '',
      encargo: usuario.encargo || 'trabajadores',
      rol: usuario.rol || 'trabajadores',
      empresaId: usuario.rol === 'conexadmin' ? '' : (usuario.empresaId ? usuario.empresaId.toString() : '')
    });
    setShowForm(true);
    setShowPassword(false);
  };

  const handleDelete = async (usuarioId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      try {
        await apiRequest(`/users/${usuarioId}`, {
          method: 'DELETE'
        });
        loadData();
      } catch (err) {
        setError('Error al eliminar usuario');
        console.error('Error deleting usuario:', err);
      }
    }
  };

  const canEditUser = (user) => {
    if (isAdmin()) return true;
    if (isEmpresaAdmin() && user.empresaId === usuario?.empresaId) return true;
    return false;
  };

  const canDeleteUser = (user) => {
    if (isAdmin()) return true;
    if (isEmpresaAdmin() && user.empresaId === usuario?.empresaId && user.rol !== 'administrador') return true;
    return false;
  };

  const getRolOptions = () => {
    if (isAdmin()) {
      return [
        { value: 'conexadmin', label: 'ConexAdmin' },
        { value: 'administrador', label: 'Administrador' },
        { value: 'trabajadores', label: 'Trabajador' },
        { value: 'cliente', label: 'Cliente' }
      ];
    }
    if (isEmpresaAdmin()) {
      return [
        { value: 'trabajadores', label: 'Trabajador' },
        { value: 'cliente', label: 'Cliente' }
      ];
    }
    return [];
  };

  const getEmpresaOptions = () => {
    if (isAdmin()) {
      return empresas.map(empresa => ({
        value: empresa.id.toString(),
        label: empresa.nombre
      }));
    }
    return [];
  };

  // Mostrar loading mientras se cargan los contextos
  if (authLoading || empresasLoading) {

    return (
      <div className="admin-container">
        <h2>Cargando...</h2>
      </div>
    );
  }

  // Verificar que el usuario esté disponible antes de verificar permisos
  if (!usuario) {

    return (
      <div className="admin-container">
        <h2>Cargando usuario...</h2>
      </div>
    );
  }

  // Verificar permisos después de que se haya cargado todo
  if (!isAdmin() && !isEmpresaAdmin()) {

    return (
      <div className="admin-container">
        <h2>No tienes permisos para acceder a esta página</h2>
      </div>
    );
  }



  try {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <h1>Administración de Usuarios</h1>
          <button
            className="btn-primary"
            onClick={() => {
              setShowForm(true);
              setEditingUsuario(null);
              setShowPassword(false);
              setFormData({
                nombres: '',
                apellidos: '',
                edad: '',
                cedulaRuc: '',
                ciudad: '',
                parroquia: '',
                usuario: '',
                email: '',
                password: '',
                encargo: 'trabajadores',
                rol: 'trabajadores',
                empresaId: isEmpresaAdmin() ? empresaActual?.id?.toString() || '' : ''
              });
            }}
          >
            Nuevo Usuario
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {showForm && (
          <div className="form-overlay">
            <div className="form-container">
              <h2>{editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nombres:</label>
                    <input
                      type="text"
                      value={formData.nombres}
                      onChange={(e) => setFormData({ ...formData, nombres: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Apellidos:</label>
                    <input
                      type="text"
                      value={formData.apellidos}
                      onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Edad:</label>
                    <input
                      type="number"
                      value={formData.edad}
                      onChange={(e) => setFormData({ ...formData, edad: e.target.value })}
                      required
                      min="18"
                    />
                  </div>
                  <div className="form-group">
                    <label>Cédula/RUC:</label>
                    <input
                      type="text"
                      value={formData.cedulaRuc}
                      onChange={(e) => setFormData({ ...formData, cedulaRuc: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Ciudad:</label>
                    <input
                      type="text"
                      value={formData.ciudad}
                      onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Parroquia:</label>
                    <input
                      type="text"
                      value={formData.parroquia}
                      onChange={(e) => setFormData({ ...formData, parroquia: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Usuario:</label>
                    <input
                      type="text"
                      value={formData.usuario}
                      onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email:</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    {editingUsuario ? 'Nueva Contraseña (dejar vacío para no cambiar):' : 'Contraseña:'}
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUsuario}
                      placeholder={editingUsuario ? 'Dejar vacío para mantener la actual' : 'Ingrese contraseña'}
                    />
                    <button
                      type="button"
                      className="btn-toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Encargo:</label>
                    <select
                      value={formData.encargo}
                      onChange={(e) => setFormData({ ...formData, encargo: e.target.value, rol: e.target.value })}
                      required
                    >
                      {getRolOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Rol:</label>
                    <select
                      value={formData.rol}
                      onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                      required
                    >
                      {getRolOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {isAdmin() && formData.rol !== 'conexadmin' && (
                  <div className="form-group">
                    <label>Empresa:</label>
                    <select
                      value={formData.empresaId}
                      onChange={(e) => setFormData({ ...formData, empresaId: e.target.value })}
                      required={formData.rol !== 'conexadmin'}
                    >
                      <option value="">Seleccionar empresa</option>
                      {getEmpresaOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {editingUsuario ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowForm(false);
                      setEditingUsuario(null);
                      setFormData({
                        nombres: '',
                        apellidos: '',
                        edad: '',
                        cedulaRuc: '',
                        ciudad: '',
                        parroquia: '',
                        usuario: '',
                        email: '',
                        password: '',
                        encargo: 'trabajadores',
                        rol: 'trabajadores',
                        empresaId: ''
                      });
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading">Cargando usuarios...</div>
        ) : (
          <div className="usuarios-table">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Usuario</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Empresa</th>
                  <th>Creado por</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((user) => (
                  <tr key={user.id}>
                    <td>{`${user.nombres} ${user.apellidos}`}</td>
                    <td>{user.usuario}</td>
                    <td>{user.email || '-'}</td>
                    <td>
                      <span className={`rol-badge rol-${user.rol.toLowerCase()}`}>
                        {user.rol}
                      </span>
                    </td>
                    <td>
                      {user.rol === 'conexadmin'
                        ? 'Sin empresa (AdminConex)'
                        : (empresas.find(e => String(e.id) === String(user.empresaId))?.nombre || 'Sin empresa')}
                    </td>
                    <td>Sistema</td>
                    <td>
                      <div className="action-buttons">
                        {canEditUser(user) && (
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(user)}
                            title="Editar"
                          >
                            <span className="icon"><i className="fas fa-edit"></i></span>
                          </button>
                        )}
                        {canDeleteUser(user) && (
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(user.id)}
                            title="Eliminar"
                          >
                            <span className="icon"><i className="fas fa-trash"></i></span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  } catch (error) {
    console.error('❌ Error en renderizado de Usuarios:', error);
    return (
      <div className="admin-container">
        <h2>Error al cargar la página</h2>
        <p>Error: {error.message}</p>
      </div>
    );
  }
};

export default Usuarios; 
